export type BannerImageFit = 'COVER' | 'CONTAIN';

export interface BannerImageFraming {
  fit: BannerImageFit;
  positionX: number;
  positionY: number;
  zoom: number;
}

export interface BannerImagePlacement {
  frameWidth: number;
  frameHeight: number;
  width: number;
  height: number;
  left: number;
  top: number;
}

export function defaultBannerImageFraming(): BannerImageFraming {
  return { fit: 'COVER', positionX: 50, positionY: 50, zoom: 1 };
}

export function normalizeBannerImageFraming(
  value: Partial<BannerImageFraming> | null | undefined
): BannerImageFraming {
  return {
    fit: value?.fit === 'CONTAIN' ? 'CONTAIN' : 'COVER',
    positionX: bounded(value?.positionX, 0, 100, 50, 0),
    positionY: bounded(value?.positionY, 0, 100, 50, 0),
    zoom: bounded(value?.zoom, 1, 3, 1, 2),
  };
}

export function bannerImagePlacement(
  frameWidth: number,
  frameHeight: number,
  naturalWidth: number,
  naturalHeight: number,
  value: Partial<BannerImageFraming> | null | undefined
): BannerImagePlacement | null {
  if (
    frameWidth <= 0 ||
    frameHeight <= 0 ||
    naturalWidth <= 0 ||
    naturalHeight <= 0
  ) {
    return null;
  }
  const framing = normalizeBannerImageFraming(value);
  const widthRatio = frameWidth / naturalWidth;
  const heightRatio = frameHeight / naturalHeight;
  const scale =
    (framing.fit === 'CONTAIN'
      ? Math.min(widthRatio, heightRatio)
      : Math.max(widthRatio, heightRatio)) * framing.zoom;
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;
  return {
    frameWidth,
    frameHeight,
    width,
    height,
    left: ((frameWidth - width) * framing.positionX) / 100,
    top: ((frameHeight - height) * framing.positionY) / 100,
  };
}

export function panBannerImageFraming(
  framing: BannerImageFraming,
  placement: BannerImagePlacement,
  dx: number,
  dy: number
): BannerImageFraming {
  const overflowX = placement.width - placement.frameWidth;
  const overflowY = placement.height - placement.frameHeight;
  return normalizeBannerImageFraming({
    ...framing,
    positionX:
      Math.abs(overflowX) < 1
        ? framing.positionX
        : framing.positionX - (dx / overflowX) * 100,
    positionY:
      Math.abs(overflowY) < 1
        ? framing.positionY
        : framing.positionY - (dy / overflowY) * 100,
  });
}

function bounded(
  value: number | undefined,
  min: number,
  max: number,
  fallback: number,
  decimals: number
): number {
  const finite = Number.isFinite(value) ? (value as number) : fallback;
  const clamped = Math.max(min, Math.min(max, finite));
  return Number(clamped.toFixed(decimals));
}
